// The Brand Ambassador ROLE (BA P1.10, WP-2b/WP-2d): who holds it in this
// company, and granting / revoking it. auth-service, through the gateway:
//
//   GET    /api/v1/ba/role-holders            → this company's holders
//   POST   /api/v1/ba/role-holders/{userId}   → grant (adds ONLY brand_ambassador)
//   DELETE /api/v1/ba/role-holders/{userId}   → revoke (removes ONLY brand_ambassador)
//
// Gated server-side by ba:profile:review in the same company; self-grant is
// refused. Takes effect on the holder's next access token (≤ 15 minutes).
//
// Contract (WP-2b, as built):
//   GET    → 200 {"holders":[{"id":"<auth user uuid>","name","email":str|null,"since":ISO|null}]}
//   POST   → 200 {"userId","role":"brand_ambassador","holder":true,"changed",
//                 "takesEffect":"next_access_token","maxDelayMinutes":15,"note"}
//   DELETE → the same shape with "holder":false
//   errors → {statusCode,error,code,message}: 403 ba_role_self_grant or a missing
//            permission, 404 ba_role_user_not_found, 422 ba_role_target_inactive /
//            ba_role_target_not_employee. Callers show `message` via getApiErrorMessage.
// `toHolder` / `unwrap` also tolerate userId / user_id and a bare list, so a
// small shape change is absorbed here and nowhere else.
//
// Users are AUTH user ids; the BA roster lists EMPLOYEES. The bridge is the
// employee record's `user_id` (employee-service): the directory search
// returns it next to the employee uuid, and /employees/names resolves a user id
// back to the employee record (its `id`), which is what a BA profile's
// `employee_id` refers to.
import { gatewayApi } from '@/lib/hrApi';
import { type ReviewerBaProfile } from '@/services/baProfileService';

const REVIEW_PROFILES = '/api/v1/employees/ba-profiles';

const HOLDERS = '/api/v1/ba/role-holders';

export interface BaRoleHolder {
  userId: string;
  name: string;
  email?: string | null;
  /** When they got the role (ISO), if the API says. */
  since: string | null;
  /** The employee record id, if the API happens to include it. */
  employeeId?: string | null;
}

const str = (v: unknown): string | null => (v === null || v === undefined || v === '' ? null : String(v));

export function toHolder(raw: any): BaRoleHolder | null {
  const userId = str(raw?.userId ?? raw?.user_id ?? raw?.id);
  if (!userId) return null;
  return {
    userId,
    name: str(raw?.name ?? raw?.displayName ?? raw?.display_name) ?? '',
    email: str(raw?.email),
    since: str(raw?.since ?? raw?.grantedAt ?? raw?.granted_at),
    employeeId: str(raw?.employeeId ?? raw?.employee_id),
  };
}

const unwrap = (data: any): any[] =>
  Array.isArray(data) ? data
    : Array.isArray(data?.holders) ? data.holders
      : Array.isArray(data?.items) ? data.items
        : Array.isArray(data?.data) ? data.data
          : [];

export const listBaRoleHolders = async (): Promise<BaRoleHolder[]> =>
  unwrap((await gatewayApi.get(HOLDERS)).data).map(toHolder).filter((h): h is BaRoleHolder => !!h);

/** POST / DELETE answer. `changed: false` = it was already so (idempotent). */
export interface BaRoleChange {
  userId: string;
  role: 'brand_ambassador';
  holder: boolean;
  changed: boolean;
  takesEffect?: string;
  maxDelayMinutes?: number;
  note?: string;
}

/** `userId` is the AUTH user id, never the employee record id. */
export const grantBaRole = async (userId: string): Promise<BaRoleChange> =>
  (await gatewayApi.post<BaRoleChange>(`${HOLDERS}/${encodeURIComponent(userId)}`)).data;

export const revokeBaRole = async (userId: string): Promise<BaRoleChange> =>
  (await gatewayApi.delete<BaRoleChange>(`${HOLDERS}/${encodeURIComponent(userId)}`)).data;

/** The employee record id for an auth user (null when there is none). */
export async function employeeIdForUser(holder: BaRoleHolder): Promise<string | null> {
  if (holder.employeeId) return holder.employeeId;
  const { data } = await gatewayApi.post<{ items?: { id: string; user_id: string | null }[] }>(
    '/api/v1/employees/names', { user_ids: [holder.userId], employee_ids: [] });
  return (data?.items ?? []).find((it) => it.user_id === holder.userId)?.id ?? null;
}

/** The holder's LIVE (published) portfolio, asked by employee directly (never
 *  inferred from the capped roster list). null ONLY when there is none: a
 *  published portfolio outside this reviewer's reach, or an answer that can't
 *  say, THROWS, so the caller reports it as still published, never as "none". */
export async function publishedProfileForHolder(holder: BaRoleHolder): Promise<ReviewerBaProfile | null> {
  const employeeId = await employeeIdForUser(holder);
  if (!employeeId) return null; // no employee record: no BA profile can exist
  const { data } = await gatewayApi.get<{ profiles: ReviewerBaProfile[]; unreachable?: number }>(REVIEW_PROFILES, {
    params: { status: 'published', employee_id: employeeId },
  });
  // Shaped so getApiErrorMessage shows the message (it reads data.message).
  const undetermined = (message: string) => Object.assign(new Error(message), { data: { message } });
  if (typeof data?.unreachable !== 'number') {
    throw undetermined('We couldn’t check whether they have a published portfolio.');
  }
  if (data.unreachable > 0) {
    throw undetermined('Their portfolio is outside the companies you review, so it wasn’t unpublished.');
  }
  return (data.profiles ?? []).find((p) => p.employee_id === employeeId && p.publication.status === 'published') ?? null;
}
